import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store/store';
import { fetchTeamPresence } from '../slices/overviewSlice';
import { initialsOf, colorFor } from '../utils/avatar';

const WEEKDAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const pad = (n: number) => String(n).padStart(2, '0');
const keyOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Montag der Woche zu einem Datum
const mondayOf = (d: Date) => {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  m.setHours(0, 0, 0, 0);
  return m;
};
const addDays = (d: Date, n: number) => {
  const r = new Date(d);
  r.setDate(d.getDate() + n);
  return r;
};
const ddmm = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;

const Avatar: React.FC<{ id: string; name: string }> = ({ id, name }) => (
  <span
    style={{
      width: 26, height: 26, borderRadius: '50%', background: colorFor(id),
      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 600, flex: '0 0 auto',
    }}
  >
    {initialsOf(name)}
  </span>
);

const TeamPresencePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { team, teamLoading, teamError } = useSelector((state: RootState) => state.overview);

  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  const todayKey = keyOf(new Date());

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = addDays(weekStart, i);
        return { date: keyOf(d), label: WEEKDAY_NAMES[i], ddmm: ddmm(d) };
      }),
    [weekStart]
  );

  const from = weekDays[0].date;
  const to = weekDays[6].date;

  useEffect(() => {
    dispatch(fetchTeamPresence({ from, to }));
  }, [dispatch, from, to]);

  // pro Tag: Personen, die arbeiten
  const peoplePerDay = useMemo(() => {
    const map: Record<string, { userId: string; username: string }[]> = {};
    weekDays.forEach((d) => (map[d.date] = []));
    (team?.people ?? [])
      .forEach((p) => {
        p.dates.forEach((date) => {
          if (map[date]) map[date].push({ userId: p.userId, username: p.username });
        });
      });
    return map;
  }, [team, weekDays]);

  const weekEndD = addDays(weekStart, 6);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 className="mb-0">Wer ist wann da?</h3>
          <div className="text-muted" style={{ fontSize: 13 }}>Präsenz des Teams pro Tag.</div>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline-secondary" onClick={() => setWeekStart(addDays(weekStart, -7))} title="Vorherige Woche">‹</button>
          <button className="btn btn-outline-secondary fw-semibold" disabled style={{ minWidth: 210, opacity: 1 }}>
            {ddmm(weekStart)} – {ddmm(weekEndD)}
          </button>
          <button className="btn btn-outline-secondary" onClick={() => setWeekStart(addDays(weekStart, 7))} title="Nächste Woche">›</button>
        </div>
      </div>

      <div className="mb-2">
        <button className="btn btn-sm btn-link text-decoration-none px-0" onClick={() => setWeekStart(mondayOf(new Date()))}>
          → Diese Woche
        </button>
      </div>

      {teamError && <div className="alert alert-danger">{teamError}</div>}

      {teamLoading && !team ? (
        <p className="text-muted">Lädt…</p>
      ) : (
        <div
          className="team-presence-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
          }}
        >
          {weekDays.map((d) => {
            const people = peoplePerDay[d.date] || [];
            const isToday = d.date === todayKey;
            return (
              <div
                key={d.date}
                className="card"
                style={{
                  minWidth: 0,
                  borderColor: isToday ? '#0d6efd' : '#e9ecef',
                  borderWidth: isToday ? 2 : 1,
                }}
              >
                <div
                  className="card-header d-flex justify-content-between align-items-center py-2"
                  style={{ background: isToday ? '#e7f1ff' : '#f8f9fa' }}
                >
                  <span>
                    <span className="fw-semibold">{d.label}</span>
                    <span className="text-muted ms-1" style={{ fontSize: 12 }}>{d.ddmm}</span>
                  </span>
                </div>
                <div className="card-body py-2 px-3">
                  {people.length > 0 && (
                    <>
                      <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                        {people.length} {people.length === 1 ? 'Person' : 'Personen'}
                      </div>
                      {people.map((p) => (
                        <div key={p.userId} className="d-flex align-items-center gap-2 mb-2">
                          <Avatar id={p.userId} name={p.username} />
                          <span style={{ fontSize: 14 }}>{p.username}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamPresencePage;
